import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import type { Auth } from "@/utils/auth";
import { navigationContent } from "./app/navigation-content";
import { userTabAccessQueryOptions } from "./api/selectUserTabAccess";
import type { TabKeyT } from "./tabs";

/**
 * Route guard for a navigation tab: the same check the nav uses, so a hidden tab
 * cannot be opened by URL. Sends the user to their first visible tab, or Profile.
 */
export async function requireTab(context: { auth: Auth; queryClient: QueryClient } | undefined, tab: TabKeyT) {
    const userId = context?.auth?.id;
    if (!userId || !context?.queryClient) {
        throw redirect({ to: "/login" });
    }

    let tabs = await context.queryClient.ensureQueryData(userTabAccessQueryOptions(userId));
    if (tabs.includes(tab)) return;

    // The cached list can predate a role change (e.g. a new clinician finishing onboarding), so check fresh before redirecting.
    tabs = await context.queryClient.fetchQuery({ ...userTabAccessQueryOptions(userId), staleTime: 0 });
    if (tabs.includes(tab)) return;

    const home = navigationContent.find((page) => page.tab && page.tab !== tab && tabs.includes(page.tab));
    throw redirect({ href: home?.href ?? "/profile", replace: true });
}
